import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { HiUserGroup, HiOfficeBuilding } from 'react-icons/hi';
import { BsCheckCircleFill, BsArrowRight } from 'react-icons/bs';
import { Mail, ArrowLeft } from 'lucide-react';
import { authApi } from '../services/api';

const Register = () => {
  const navigate = useNavigate();
  const { registerCommunity, registerBusiness } = useAuth();
  const [step, setStep] = useState(1); // 1 = choose type, 2 = form, 3 = verify email
  const [accountType, setAccountType] = useState(null);
  const [loading, setLoading] = useState(false);
  const [registeredEmail, setRegisteredEmail] = useState('');
  const [resending, setResending] = useState(false);

  // Community form
  const [communityForm, setCommunityForm] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
    zip_code: ''
  });

  // Business form
  const [businessForm, setBusinessForm] = useState({
    business_name: '',
    owner_name: '',
    email: '',
    password: '',
    confirmPassword: '',
    phone: '',
    business_address: ''
  });

  const handleCommunitySubmit = async (e) => {
    e.preventDefault();
    if (communityForm.password !== communityForm.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    
    setLoading(true);
    try {
      await registerCommunity({
        name: communityForm.name,
        email: communityForm.email,
        password: communityForm.password,
        zip_code: communityForm.zip_code
      });
      setRegisteredEmail(communityForm.email);
      setStep(3);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  const handleBusinessSubmit = async (e) => {
    e.preventDefault();
    if (businessForm.password !== businessForm.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    
    setLoading(true);
    try {
      await registerBusiness({
        business_name: businessForm.business_name,
        owner_name: businessForm.owner_name,
        email: businessForm.email,
        password: businessForm.password,
        phone: businessForm.phone,
        business_address: businessForm.business_address
      });
      setRegisteredEmail(businessForm.email);
      setStep(3);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  const handleResendVerification = async () => {
    setResending(true);
    try {
      await authApi.resendVerification(registeredEmail);
      toast.success('Verification email sent!');
    } catch (error) {
      toast.error('Failed to resend. Please try again.');
    } finally {
      setResending(false);
    }
  };

  return (
    <div className="min-h-screen bg-neutral-50 py-12" data-testid="register-page">
      <Helmet>
        <title>Register Your Business or Join the Community | Explore MHC - Martinsville, VA</title>
        <meta name="description" content="Create a free account to list your business or share community events in Martinsville and Henry County, Virginia. Join Explore MHC today." />
        <link rel="canonical" href="https://exploremhc.com/register" />
      </Helmet>
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-4xl">
        {step === 3 ? (
          /* ===== EMAIL VERIFICATION STEP ===== */
          <div className="max-w-md mx-auto animate-fade-in text-center" data-testid="verify-email-prompt">
            <div className="bg-white rounded-2xl p-8 border border-neutral-200">
              <div className="w-20 h-20 rounded-full bg-[#005AA0]/10 flex items-center justify-center mx-auto mb-6">
                <Mail className="w-10 h-10 text-[#005AA0]" />
              </div>
              <h2 className="text-2xl font-bold text-neutral-900 mb-3" style={{ fontFamily: 'DM Serif Display, serif' }}>
                Check Your Email
              </h2>
              <p className="text-neutral-600 mb-2">
                We've sent a verification link to:
              </p>
              <p className="font-semibold text-[#005AA0] mb-6">{registeredEmail}</p>
              <p className="text-sm text-neutral-500 mb-6">
                Click the link in your email to verify your account and start using Explore MHC.
              </p>
              <div className="space-y-3">
                <button
                  onClick={handleResendVerification}
                  disabled={resending}
                  className="w-full px-6 py-3 border-2 border-neutral-200 text-neutral-700 rounded-full font-semibold hover:bg-neutral-50 transition-colors disabled:opacity-50"
                  data-testid="resend-verification-btn"
                >
                  {resending ? 'Sending...' : 'Resend Verification Email'}
                </button>
                <Link
                  to={accountType === 'business' ? '/business-login' : '/community-login'}
                  className="block w-full px-6 py-3 bg-[#005AA0] text-white rounded-full font-semibold hover:bg-[#004B87] transition-colors text-center"
                  data-testid="go-to-login-btn"
                >
                  Go to Login
                </Link>
              </div>
            </div>
          </div>
        ) : step === 1 ? (
          <div className="animate-fade-in">
            <div className="text-center mb-12">
              <h1 className="text-4xl font-bold text-neutral-900 mb-4" style={{ fontFamily: 'DM Serif Display, serif' }}>
                Join Explore MHC
              </h1>
              <p className="text-lg text-neutral-600">
                Choose how you want to be part of our community
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              {/* Community Member Card */}
              <button
                onClick={() => { setAccountType('community'); setStep(2); }}
                className={`text-left p-8 bg-white rounded-2xl border-2 transition-all hover:border-primary hover:shadow-lg ${
                  accountType === 'community' ? 'border-primary' : 'border-neutral-200'
                }`}
                data-testid="select-community-member"
              >
                <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center mb-6">
                  <HiUserGroup className="w-8 h-8 text-primary" />
                </div>
                <h2 className="text-2xl font-bold text-neutral-900 mb-3">Community Member</h2>
                <p className="text-neutral-600 mb-6">
                  Stay connected with local events and support local businesses
                </p>
                <ul className="space-y-3 text-sm text-neutral-600">
                  <li className="flex items-center gap-2">
                    <BsCheckCircleFill className="w-4 h-4 text-secondary" /> Submit community events
                  </li>
                  <li className="flex items-center gap-2">
                    <BsCheckCircleFill className="w-4 h-4 text-secondary" /> Feature your events
                  </li>
                  <li className="flex items-center gap-2">
                    <BsCheckCircleFill className="w-4 h-4 text-secondary" /> Stay updated on local happenings
                  </li>
                </ul>
                <div className="mt-6 flex items-center gap-2 text-primary font-semibold">
                  Get Started <BsArrowRight className="w-4 h-4" />
                </div>
              </button>

              {/* Business Owner Card */}
              <button
                onClick={() => { setAccountType('business'); setStep(2); }}
                className={`text-left p-8 bg-white rounded-2xl border-2 transition-all hover:border-secondary hover:shadow-lg ${
                  accountType === 'business' ? 'border-secondary' : 'border-neutral-200'
                }`}
                data-testid="select-business-owner"
              >
                <div className="w-16 h-16 rounded-2xl bg-secondary/10 flex items-center justify-center mb-6">
                  <HiOfficeBuilding className="w-8 h-8 text-secondary" />
                </div>
                <h2 className="text-2xl font-bold text-neutral-900 mb-3">Business Owner</h2>
                <p className="text-neutral-600 mb-6">
                  List your business and reach more local customers
                </p>
                <ul className="space-y-3 text-sm text-neutral-600">
                  <li className="flex items-center gap-2">
                    <BsCheckCircleFill className="w-4 h-4 text-secondary" /> Create a business listing
                  </li>
                  <li className="flex items-center gap-2">
                    <BsCheckCircleFill className="w-4 h-4 text-secondary" /> Submit events for your business
                  </li>
                  <li className="flex items-center gap-2">
                    <BsCheckCircleFill className="w-4 h-4 text-secondary" /> Get featured placement
                  </li>
                  <li className="flex items-center gap-2">
                    <BsCheckCircleFill className="w-4 h-4 text-accent" /> First 5 businesses get FREE featured month!
                  </li>
                </ul>
                <div className="mt-6 flex items-center gap-2 text-secondary font-semibold">
                  Get Started <BsArrowRight className="w-4 h-4" />
                </div>
              </button>
            </div>

            <p className="text-center mt-8 text-neutral-600">
              Already have an account?{' '}
              <Link to="/community-login" className="text-primary font-semibold hover:underline">
                Sign In
              </Link>
            </p>
          </div>
        ) : accountType === 'community' ? (
          <div className="max-w-md mx-auto animate-fade-in">
            <button
              onClick={() => setStep(1)}
              className="text-neutral-600 hover:text-primary mb-6 flex items-center gap-2"
            >
              ← Back
            </button>
            
            <div className="bg-white rounded-2xl p-8 border border-neutral-200">
              <div className="text-center mb-8">
                <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center mx-auto mb-4">
                  <HiUserGroup className="w-8 h-8 text-primary" />
                </div>
                <h2 className="text-2xl font-bold text-neutral-900">Community Member</h2>
                <p className="text-neutral-600">Create your free account</p>
              </div>

              <form onSubmit={handleCommunitySubmit} className="space-y-4">
                <div>
                  <label className="form-label">Full Name</label>
                  <input
                    type="text"
                    required
                    value={communityForm.name}
                    onChange={(e) => setCommunityForm({ ...communityForm, name: e.target.value })}
                    className="form-input"
                    data-testid="community-name-input"
                  />
                </div>

                <div>
                  <label className="form-label">Email</label>
                  <input
                    type="email"
                    required
                    value={communityForm.email}
                    onChange={(e) => setCommunityForm({ ...communityForm, email: e.target.value })}
                    className="form-input"
                    data-testid="community-email-input"
                  />
                </div>

                <div>
                  <label className="form-label">ZIP Code</label>
                  <input
                    type="text"
                    required
                    value={communityForm.zip_code}
                    onChange={(e) => setCommunityForm({ ...communityForm, zip_code: e.target.value })}
                    className="form-input"
                    data-testid="community-zip-input"
                  />
                </div>

                <div>
                  <label className="form-label">Password</label>
                  <input
                    type="password"
                    required
                    minLength={6}
                    value={communityForm.password}
                    onChange={(e) => setCommunityForm({ ...communityForm, password: e.target.value })}
                    className="form-input"
                    data-testid="community-password-input"
                  />
                </div>

                <div>
                  <label className="form-label">Confirm Password</label>
                  <input
                    type="password"
                    required
                    value={communityForm.confirmPassword}
                    onChange={(e) => setCommunityForm({ ...communityForm, confirmPassword: e.target.value })}
                    className="form-input"
                    data-testid="community-confirm-password-input"
                  />
                </div>

                <button 
                  type="submit" 
                  className="btn-primary w-full"
                  disabled={loading}
                  data-testid="community-register-btn"
                >
                  {loading ? 'Creating Account...' : 'Create Account'}
                </button>
              </form>
            </div>
          </div>
        ) : (
          <div className="max-w-md mx-auto animate-fade-in">
            <button
              onClick={() => setStep(1)}
              className="text-neutral-600 hover:text-primary mb-6 flex items-center gap-2"
            >
              ← Back
            </button>
            
            <div className="bg-white rounded-2xl p-8 border border-neutral-200">
              <div className="text-center mb-8">
                <div className="w-16 h-16 rounded-2xl bg-secondary/10 flex items-center justify-center mx-auto mb-4">
                  <HiOfficeBuilding className="w-8 h-8 text-secondary" />
                </div>
                <h2 className="text-2xl font-bold text-neutral-900">Business Owner</h2>
                <p className="text-neutral-600">Create your business account</p>
              </div>

              <form onSubmit={handleBusinessSubmit} className="space-y-4">
                <div>
                  <label className="form-label">Business Name</label>
                  <input
                    type="text"
                    required
                    value={businessForm.business_name}
                    onChange={(e) => setBusinessForm({ ...businessForm, business_name: e.target.value })}
                    className="form-input"
                    data-testid="business-name-input"
                  />
                </div>

                <div>
                  <label className="form-label">Owner Name</label>
                  <input
                    type="text"
                    required
                    value={businessForm.owner_name}
                    onChange={(e) => setBusinessForm({ ...businessForm, owner_name: e.target.value })}
                    className="form-input"
                    data-testid="business-owner-name-input"
                  />
                </div>

                <div>
                  <label className="form-label">Email</label>
                  <input
                    type="email"
                    required
                    value={businessForm.email}
                    onChange={(e) => setBusinessForm({ ...businessForm, email: e.target.value })}
                    className="form-input"
                    data-testid="business-email-input"
                  />
                </div>

                <div>
                  <label className="form-label">Phone</label>
                  <input
                    type="tel"
                    required
                    value={businessForm.phone}
                    onChange={(e) => setBusinessForm({ ...businessForm, phone: e.target.value })}
                    className="form-input"
                    data-testid="business-phone-input"
                  />
                </div>

                <div>
                  <label className="form-label">Business Address</label>
                  <input
                    type="text"
                    required
                    value={businessForm.business_address}
                    onChange={(e) => setBusinessForm({ ...businessForm, business_address: e.target.value })}
                    className="form-input"
                    data-testid="business-address-input"
                  />
                </div>

                <div>
                  <label className="form-label">Password</label>
                  <input
                    type="password"
                    required
                    minLength={6}
                    value={businessForm.password}
                    onChange={(e) => setBusinessForm({ ...businessForm, password: e.target.value })}
                    className="form-input"
                    data-testid="business-password-input"
                  />
                </div>

                <div>
                  <label className="form-label">Confirm Password</label>
                  <input
                    type="password"
                    required
                    value={businessForm.confirmPassword}
                    onChange={(e) => setBusinessForm({ ...businessForm, confirmPassword: e.target.value })}
                    className="form-input"
                    data-testid="business-confirm-password-input"
                  />
                </div>

                <button 
                  type="submit" 
                  className="btn-primary w-full bg-secondary hover:bg-secondary-light"
                  disabled={loading}
                  data-testid="business-register-btn"
                >
                  {loading ? 'Creating Account...' : 'Create Business Account'}
                </button>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Register;
